/**
 * Sidebar row trigger — same look as a regular `NavItem` (icon + label,
 * collapsed/expanded variants, hover + active styles) but clicking it
 * opens the `NotificationTray` in a side popover instead of routing.
 *
 * The row still highlights when the user is on `/notification-admin` so
 * the "you are here" affordance survives. The tray's footer CTA does the
 * actual navigate.
 *
 * Mounted by `MainNavigation` in place of the legacy `to:` entry.
 */
import React from 'react';
import { Bell } from 'lucide-react';
import { useLocation } from 'react-router';
import { cn } from '@/lib/utils';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useTranslation } from '@/modules/shared/i18n/useTranslation';
import { en, th } from './locales';
import NotificationTray from './NotificationTray';
import { useNotificationStreamContext } from './NotificationStreamProvider';
import { testIds } from '@/test/testIds';

interface Props {
  label: string;
  isCollapsed: boolean;
}

const formatCount = (count: number): string => {
  if (count <= 0) return '';
  if (count > 99) return '99+';
  return String(count);
};

const NotificationSidebarItem: React.FC<Props> = ({ label, isCollapsed }) => {
  const { t } = useTranslation({ en, th });
  // The tab's single stream, owned by NotificationStreamProvider above the router.
  const stream = useNotificationStreamContext();
  const location = useLocation();
  const [open, setOpen] = React.useState(false);

  const active = location.pathname === '/notification-admin';
  const count = stream.unreadCount;

  // First-open permission ask — same one-shot guard as the legacy bell.
  const requestedRef = React.useRef(false);
  const handleOpenChange = (nextOpen: boolean) => {
    setOpen(nextOpen);
    if (
      nextOpen &&
      !requestedRef.current &&
      typeof Notification !== 'undefined' &&
      Notification.permission === 'default'
    ) {
      requestedRef.current = true;
      try {
        void Notification.requestPermission();
      } catch {
        /* older browsers / blocked */
      }
    }
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={t('ariaBell')}
          data-testid={testIds.notificationsTray.bell}
          className={cn(
            'flex items-center space-x-3 px-2.5 py-2 rounded-[8px] mx-2 my-1 w-[calc(100%-1rem)] transition-all duration-200 font-prompt hover:text-foreground hover:bg-accent',
            active ? 'text-foreground bg-accent' : 'text-foreground/75 dark:text-muted-foreground',
            isCollapsed && 'justify-center px-1 mx-1 w-auto',
          )}
        >
          <span className="relative flex-shrink-0">
            <Bell className="h-5 w-5" />
            {isCollapsed && count > 0 && (
              <span
                className="absolute -right-1.5 -top-1.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-2xs font-semibold leading-none text-primary-foreground"
                aria-label={`${count} unread`}
              >
                {formatCount(count)}
              </span>
            )}
          </span>
          {!isCollapsed && (
            <span className="flex flex-1 items-center justify-between text-sm font-medium">
              <span>{label}</span>
              {count > 0 && (
                <span
                  className="ml-2 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-2xs font-semibold leading-none text-primary-foreground"
                  aria-hidden="true"
                >
                  {formatCount(count)}
                </span>
              )}
            </span>
          )}
        </button>
      </PopoverTrigger>
      {/* Anchor the popover's END (bottom edge) to the trigger bottom and
          pull it DOWN further so the tray hangs over the sidebar footer
          area instead of floating in the upper viewport. `alignOffset` is
          negative because radix's "end" baseline lives at the trigger end,
          and a negative value extends past it (downward when side=right).
          `collisionPadding` keeps a 16px gutter from viewport edges. */}
      <PopoverContent
        side="right"
        align="end"
        sideOffset={12}
        alignOffset={-120}
        collisionPadding={16}
        className="w-auto max-h-none overflow-visible p-0"
      >
        <NotificationTray stream={stream} onClose={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
};

export default NotificationSidebarItem;
